import React, { useState, useEffect, useRef } from 'react';
import { TrialBar, TrialUpgradeModal, ViewerBlockedNotice } from './components/TrialUpgrade';
import { useNavigate, useLocation } from 'react-router-dom';
import { BRAND } from './product';
import { useAuth } from './AuthContext';
import { useBackgroundJobs, BackgroundOperation } from './BackgroundJobsContext';
import Sidebar from './Sidebar';
import CreditBalance from './CreditBalance';
import FeatureAnnouncementModal from './components/FeatureAnnouncementModal';
import AccessGate from './components/AccessGate';

interface AppShellProps {
  children: React.ReactNode;
  /** If true, hides the top header bar (used by project workspace which has its own header) */
  hideHeader?: boolean;
  /** If true, removes max-width constraint for full-width layouts */
  fullWidth?: boolean;
  /** Subscription warning state — passed from parent */
  showSubscriptionWarning?: boolean;
}

const AppShell: React.FC<AppShellProps> = ({
  children,
  hideHeader = false,
  fullWidth = false,
  showSubscriptionWarning = false,
}) => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const { operations, activeCount, completedCount, hasUndismissed, dismissOperation, dismissAll } = useBackgroundJobs();
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebar-collapsed');
    return saved === 'true';
  });
  const [subscriptionWarning, setSubscriptionWarning] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);

  // Phones (< 768px, Tailwind's md): the sidebar is a drawer behind the top
  // bar's ☰, always shown expanded. Laptops and desktops are unchanged.
  const location = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const on = () => setIsMobile(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', on) : mq.addListener(on);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', on) : mq.removeListener(on); };
  }, []);
  // Close the drawer whenever the page changes (a menu item was tapped).
  useEffect(() => { setMobileNavOpen(false); }, [location.pathname, location.search]);

  // Persist sidebar state
  useEffect(() => {
    localStorage.setItem('sidebar-collapsed', String(collapsed));
  }, [collapsed]);

  // Fetch subscription warning status
  useEffect(() => {
    const fetchSubscriptionStatus = async () => {
      if (!token) return;
      try {
        const response = await fetch('/api/stripe/subscription-status', {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setSubscriptionWarning(data.show_subscription_warning || false);
        }
      } catch (err) {
        console.error('Failed to fetch subscription status');
      }
    };
    fetchSubscriptionStatus();
  }, [token]);

  // Close notification panel when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };
    if (showNotifications) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showNotifications]);

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    return date.toLocaleDateString();
  };

  const getStatusIcon = (op: BackgroundOperation) => {
    if (op.status === 'processing') {
      return (
        <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
          <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
      );
    }
    if (op.status === 'completed') {
      return (
        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
          <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
      );
    }
    return (
      <div className="w-8 h-8 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
        <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </div>
    );
  };

  const totalBadge = operations.length;

  return (
    <div className="min-h-screen bg-gray-50/80">
      <Sidebar
        collapsed={collapsed && !isMobile}
        onToggleCollapse={() => setCollapsed(!collapsed)}
        mobileOpen={mobileNavOpen}
        onMobileClose={() => setMobileNavOpen(false)}
      />
      {mobileNavOpen && (
        <div className="md:hidden fixed inset-0 bg-black/30 z-[69]" onClick={() => setMobileNavOpen(false)} aria-hidden="true" />
      )}

      {/* Main content area — offset by sidebar width (none on phones) */}
      <div
        className={`transition-all duration-200 ${collapsed ? 'ml-16' : 'ml-60'} max-md:ml-0`}
      >
        <TrialBar />
        <TrialUpgradeModal />
        <ViewerBlockedNotice />
        {/* Subscription Warning Banner */}
        {subscriptionWarning && !warningDismissed && (
          <div className="bg-amber-50 border-b border-amber-200">
            <div className="px-6 py-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-sm text-amber-800">
                    <span className="font-medium">Subscription Required:</span> Your account does not have an active subscription.{' '}
                    <button
                      onClick={() => navigate('/settings')}
                      className="underline hover:text-amber-900 font-medium"
                    >
                      Subscribe now
                    </button>{' '}
                    to ensure continued access.
                  </p>
                </div>
                <button
                  onClick={() => setWarningDismissed(true)}
                  className="text-amber-600 hover:text-amber-800 p-1"
                  title="Dismiss"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Top bar with credits and notifications */}
        <div className="flex items-center justify-end gap-4 px-6 py-2 border-b border-gray-200/60 bg-white/80 backdrop-blur-sm max-md:sticky max-md:top-0 max-md:z-20 max-md:px-3 max-md:gap-2">
          {/* Phones: menu button + logo (the sidebar is a drawer) */}
          <div className="md:hidden flex items-center gap-2 mr-auto min-w-0">
            <button
              onClick={() => setMobileNavOpen(true)}
              className="p-2 -ml-1 rounded-lg text-gray-600 hover:bg-gray-100"
              aria-label="Open menu"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <img src={BRAND.icon} alt={BRAND.name} className="w-7 h-7 rounded-lg flex-shrink-0" />
          </div>
          {/* Notification Bell */}
          <div className="relative" ref={notificationRef}>
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className={`relative p-2 rounded-lg transition-colors ${
                showNotifications ? 'bg-gray-100' : 'hover:bg-gray-50'
              } ${activeCount > 0 ? 'text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
              title={`${activeCount} running, ${completedCount} completed`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              {totalBadge > 0 && (
                <span className={`absolute -top-1 -right-1 inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white rounded-full ${
                  activeCount > 0 ? 'bg-blue-600 animate-pulse' : 'bg-green-500'
                }`}>
                  {totalBadge}
                </span>
              )}
            </button>

            {/* Notification Panel */}
            {showNotifications && (
              <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-modal border border-gray-200/80 z-50 overflow-hidden animate-scale-in max-md:fixed max-md:left-3 max-md:right-3 max-md:top-14 max-md:w-auto">
                <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                  <h3 className="text-sm font-semibold text-gray-900">Background Jobs</h3>
                  <div className="flex items-center gap-2">
                    {activeCount > 0 && (
                      <span className="text-xs text-blue-600 font-medium">{activeCount} running</span>
                    )}
                    {completedCount > 0 && (
                      <button
                        onClick={dismissAll}
                        className="text-xs text-gray-500 hover:text-gray-700 hover:underline"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>

                <div className="max-h-96 overflow-y-auto">
                  {operations.length === 0 ? (
                    <div className="px-4 py-8 text-center text-sm text-gray-400">
                      No background jobs
                    </div>
                  ) : (
                    operations.map(op => (
                      <div
                        key={op.id}
                        className={`px-4 py-3 border-b border-gray-50 last:border-0 ${
                          op.status === 'processing' ? 'bg-blue-50/30' : ''
                        }`}
                      >
                        <div className="flex items-start gap-3">
                          {getStatusIcon(op)}
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {op.job_name}
                            </p>
                            <p className="text-xs text-gray-500 truncate">
                              → {op.project_name}
                            </p>

                            {/* Progress bar for processing */}
                            {op.status === 'processing' && (
                              <div className="mt-2">
                                <div className="w-full bg-gray-200 rounded-full h-1.5">
                                  <div
                                    className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
                                    style={{
                                      width: `${op.total_records > 0 ? Math.round((op.processed_records / op.total_records) * 100) : 0}%`
                                    }}
                                  ></div>
                                </div>
                                <p className="text-xs text-gray-500 mt-1">
                                  {op.processed_records} / {op.total_records} processed
                                </p>
                              </div>
                            )}

                            {/* Completion summary */}
                            {op.status === 'completed' && (
                              <div className="flex gap-3 mt-1 text-xs">
                                {op.added_count > 0 && (
                                  <span className="text-green-600">{op.added_count} added</span>
                                )}
                                {op.updated_count > 0 && (
                                  <span className="text-blue-600">{op.updated_count} updated</span>
                                )}
                                {op.skipped_count > 0 && (
                                  <span className="text-amber-600">{op.skipped_count} skipped</span>
                                )}
                              </div>
                            )}

                            {/* Error */}
                            {op.status === 'error' && (
                              <p className="text-xs text-red-500 mt-1">
                                Error — {op.added_count} added, {op.updated_count} updated before failure
                              </p>
                            )}

                            <p className="text-xs text-gray-400 mt-1">{formatTime(op.created_at)}</p>
                          </div>

                          {/* Dismiss button for completed/error */}
                          {(op.status === 'completed' || op.status === 'error') && op.id > 0 && (
                            <button
                              onClick={() => dismissOperation(op.id)}
                              className="text-gray-300 hover:text-gray-500 p-1 flex-shrink-0"
                              title="Dismiss"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <CreditBalance />
        </div>

        {/* Page Content */}
        <main className={`${fullWidth ? 'px-6 py-6' : 'max-w-7xl mx-auto px-6 py-8'} max-md:px-3 max-md:py-4`}>
          {children}
        </main>
      </div>

      <FeatureAnnouncementModal />
      {/* Lapsed plan → "choose a plan" (trial ended / plan ended / payment failed) */}
      <AccessGate />
    </div>
  );
};

export default AppShell;
